import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { CellWrapper, CellWithoutDataContent, EllipsisText } from './../styles';
import Input from '../StyledMaskedInput';

const StyledInput = styled(Input)`
  border-color: ${(props) => (props.hasError ? '#dc2626' : '#d1d5db')};
  
  &:focus {
    border-color: ${(props) => (props.hasError ? '#dc2626' : '#3b82f6')};
    box-shadow: 0 0 0 2px ${(props) => (props.hasError ? 'rgba(220, 38, 38, 0.1)' : 'rgba(59, 130, 246, 0.1)')};
  }
`;

const ErrorMessage = styled.div`
  color: #dc2626;
  font-size: 0.75rem;
  margin-top: 0.25rem;
  position: absolute;
  background: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 10;
  white-space: nowrap;
  animation: ${(props) => (props.shake ? 'shake 0.5s ease-in-out' : 'none')};
  
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-5px); }
    75% { transform: translateX(5px); }
  }
`;

const InputContainer = styled.div`
  position: relative;
`;

DateInput.defaultProps = {
  value: '',
  editable: false,
  onChange: null,
  isMissing: false,
  isActive: false,
  isEditing: undefined,
  onStartEditing: null,
  onStopEditing: null,
  rowIndex: undefined,
  colIndex: undefined,
  registerCellRef: null,
};

DateInput.propTypes = {
  value: PropTypes.string,
  editable: PropTypes.bool,
  onChange: PropTypes.func,
  isMissing: PropTypes.bool,
  isActive: PropTypes.bool,
  isEditing: PropTypes.bool,
  onStartEditing: PropTypes.func,
  onStopEditing: PropTypes.func,
  rowIndex: PropTypes.number,
  colIndex: PropTypes.number,
  registerCellRef: PropTypes.func,
};

function DateInput(props) {
  const {
    value: propValue,
    editable: propEditable,
    onChange,
    isMissing,
    isActive = false,
    isEditing = false,
    onStartEditing,
    onStopEditing,
    rowIndex,
    colIndex,
    registerCellRef,
  } = props;
  const [value, setValue] = useState(propValue);
  const [initialValue, setInitialValue] = useState(propValue || '');
  const [editable, setEditable] = useState(!!propEditable || isEditing);
  const [validationError, setValidationError] = useState('');
  const [shakeError, setShakeError] = useState(false);
  const inputRef = useRef(null);
  const cellRef = useRef(null);
  const isSavingRef = useRef(false);

  useEffect(() => {
    if (registerCellRef && typeof rowIndex === 'number' && typeof colIndex === 'number') {
      registerCellRef(rowIndex, colIndex, cellRef.current);

      return () => registerCellRef(rowIndex, colIndex, null);
    }

    return undefined;
  }, [registerCellRef, rowIndex, colIndex]);

  const isCurrentlyEditing = isEditing !== undefined ? isEditing : editable;

  useEffect(() => {
    if (propValue !== value && !propEditable && isEditing === undefined) {
      setValue(propValue);
      setInitialValue(propValue);
      setEditable(false);
      validateDate(propValue);
    } else if (propValue !== value && isEditing === undefined) {
      setValue(propValue);
      setInitialValue(propValue);
      validateDate(propValue);
    }
  }, [propValue, isEditing, value]);

  useEffect(() => {
    if (isEditing !== undefined) {
      setEditable(isEditing);
      if (isEditing && inputRef.current) {
        setTimeout(() => {
          const inputElement = inputRef.current;
          if (inputElement && inputElement.focus && typeof inputElement.focus === 'function') {
            inputElement.focus();
            if (inputElement.select && typeof inputElement.select === 'function') {
              inputElement.select();
            }
          }
        }, 0);
      }
    }
  }, [isEditing]);

  const validateDate = (dateValue) => {
    if (!dateValue || dateValue.trim() === '') {
      setValidationError('');

      return true;
    }

    const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
    if (!dateRegex.test(dateValue)) {
      const error = 'Invalid date format (MM/DD/YYYY)';
      setValidationError(error);

      return false;
    }

    const [month, day, year] = dateValue.split('/').map(Number);
    const date = new Date(year, month - 1, day);

    if (date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day) {
      const error = 'Invalid date';
      setValidationError(error);

      return false;
    }

    const currentYear = new Date().getFullYear();
    if (year < 1900 || year > currentYear + 10) {
      const error = `Year must be between 1900 and ${currentYear + 10}`;
      setValidationError(error);

      return false;
    }

    setValidationError('');

    return true;
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    setValue(newValue);

    if (validationError && newValue !== value) {
      setValidationError('');
    }

    const numbers = newValue.match(/\d+/g);
    if (!numbers || newValue.trim() === '') {
      setValidationError('');
      return;
    }

    if (newValue.length >= 10) {
      validateDate(newValue);
    }
  };

  const handleBlur = (e) => {
    if (isSavingRef.current) {
      return;
    }

    const finalValue = e.target.value;
    const isValid = validateDate(finalValue);

    if (!isValid && finalValue.trim() !== '') {
      setShakeError(true);
      setTimeout(() => setShakeError(false), 500);
      return;
    }

    if (isEditing !== undefined && onStopEditing) {
      onStopEditing();
    } else {
      setEditable(false);
    }

    if (onChange && finalValue !== initialValue) {
      onChange(finalValue, isValid);
    }
  };

  const handleEnterSave = () => {
    isSavingRef.current = true;

    const isValid = validateDate(value);

    if (!isValid && value.trim() !== '') {
      setShakeError(true);
      setTimeout(() => setShakeError(false), 500);
      isSavingRef.current = false;
      return;
    }

    if (isEditing !== undefined && onStopEditing) {
      onStopEditing();
    } else {
      setEditable(false);
    }

    if (onChange) {
      onChange(value, isValid);
    }

    setTimeout(() => {
      isSavingRef.current = false;
    }, 300);
  };

  const handleEscapeCancel = () => {
    isSavingRef.current = false;
    setValue(initialValue);
    setValidationError('');
    if (isEditing !== undefined && onStopEditing) {
      onStopEditing();
    } else {
      setEditable(false);
    }
  };

  const handleEnterEdit = () => {
    if (!isCurrentlyEditing) {
      if (isEditing !== undefined && onStartEditing) {
        onStartEditing();
      } else {
        setEditable(true);
      }
    }
  };

  const edit = () => {
    if (isEditing !== undefined && onStartEditing) {
      onStartEditing();
    } else {
      setEditable(true);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isCurrentlyEditing) {
        handleEnterSave();
      } else {
        handleEnterEdit();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleEscapeCancel();
    }
  };

  return (
    <CellWrapper
      ref={cellRef}
      isActive={isActive}
      isEditing={isCurrentlyEditing}
      onKeyDown={!isCurrentlyEditing ? handleKeyDown : undefined}
      tabIndex={!isCurrentlyEditing ? 0 : -1}
    >
      {isCurrentlyEditing ? (
        <InputContainer>
          <StyledInput
            ref={inputRef}
            value={value}
            type="text"
            autoFocus
            onChange={handleChange}
            onPressEnter={handleEnterSave}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            typeOfMask="date"
            hasError={!!validationError}
          />
          {validationError && (
            <ErrorMessage shake={shakeError}>{validationError}</ErrorMessage>
          )}
        </InputContainer>
      ) : (
        <>
          {value ? (
            <EllipsisText
              onClick={edit}
              isActive={isActive}
              style={{ color: validationError ? '#dc2626' : 'inherit' }}
            >
              {value}
            </EllipsisText>
          ) : (
            <CellWithoutDataContent
              onClick={edit}
              isMissing={isMissing}
              isActive={isActive}
            />
          )}
        </>
      )}
    </CellWrapper>
  );
}

export default DateInput;
